import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/query/query-keys';
import type { TripDetail } from '@/hooks/trips/types';

interface ActivateRoamingInput {
  tripId: string;
}

async function activateRoamingRequest(tripId: string): Promise<void> {
  const response = await apiFetch(`/api/v1/business-trip-requests/${tripId}/activate-roaming`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      activate: true,
    }),
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message =
      payload && typeof payload === 'object' && 'error' in (payload as Record<string, unknown>)
        ? String(
            (payload as Record<string, { message?: string }>).error?.message ??
              'Failed to activate roaming'
          )
        : 'Failed to activate roaming';

    throw new Error(message);
  }
}

export function useActivateRoaming() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tripId }: ActivateRoamingInput) => activateRoamingRequest(tripId),
    onMutate: async ({ tripId }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.trip(tripId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.trips });
      await queryClient.cancelQueries({ queryKey: queryKeys.tripApprovals });

      const tripKey = queryKeys.trip(tripId);
      const previousTrip = queryClient.getQueryData<TripDetail>(tripKey);

      if (previousTrip) {
        const updatedTrip: TripDetail = {
          ...previousTrip,
          roamingExpense: true,
          roamingActivated: true,
        };
        queryClient.setQueryData(tripKey, updatedTrip);
      }

      return { previousTrip };
    },
    onError: (_error, { tripId }, context) => {
      if (!context) {
        return;
      }

      const tripKey = queryKeys.trip(tripId);
      if (context.previousTrip) {
        queryClient.setQueryData(tripKey, context.previousTrip);
      }
    },
    onSettled: (_result, _error, { tripId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trip(tripId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripApprovals });
    },
  });
}
